import { PlusIcon, ShieldCheckIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { OverflowTooltip } from '@/components/common/overflow-tooltip';
import { TextButton } from '@/components/common/text-button';
import { isFeatureEnabled } from '@/config/preview-features';
import { routes } from '@/config/routes';
import {
  AddParametersModal,
  firstEligibleUnitValue,
  useEligibleDevices,
} from '@/features/device-params';
import type { OrderDetail, OrderLineItem } from '@/features/orders';
import { isPrePlacementStatus } from '@/features/orders';
import { formatPrice } from '@/lib/format';

import styles from './order-summary-card.module.scss';

/** Figma `622:343265`, verbatim — the same string as the ellipsis item and the Customer toolbar
 *  button, so a menu item cannot read differently from the link beneath it (grilling Q8). */
const ADD_PARAMS = 'Add Parameter Template';

/** Figma `622:343267`, verbatim. */
const VIEW_INSTALLATION = 'View Installation';

function LineItem({
  item,
  unitValue,
  onAddParams,
  onViewInstallation,
}: {
  item: OrderLineItem;
  /** The Unit this line item's link preselects, or `null` when the eligible-units response does not
   *  name it — the **only** thing deciding whether the link renders (AC3). */
  unitValue: string | null;
  onAddParams: (unitValue: string) => void;
  /** Opens the order's installation request, or absent when this line item has none (120998). */
  onViewInstallation?: () => void;
}) {
  const bundleList = item.bundleDevices?.length
    ? item.bundleDevices.map((d) => `${d.name} × ${d.quantity}`).join(', ')
    : null;
  // The backend counts Approved units only; the frontend renders that number verbatim and never
  // aggregates it (ADR-0022). Absent/null ⇒ no label at all (story 112368).
  const replaced = item.replacedCount ?? 0;
  return (
    <li className={styles.item}>
      {item.imageUrl ? (
        <img className={styles.thumb} src={item.imageUrl} alt="" />
      ) : (
        <span className={styles.thumb} aria-hidden />
      )}
      <div className={styles.info}>
        <OverflowTooltip title={item.name}>
          <span className={styles.name}>{item.name}</span>
        </OverflowTooltip>
        {bundleList ? (
          // A Bundle's device list is clamped to two lines; the whole list is one hover away.
          <OverflowTooltip title={bundleList}>
            <span className={styles.sub}>{bundleList}</span>
          </OverflowTooltip>
        ) : null}
        {replaced > 0 ? (
          <span className={styles.replaced}>
            <ShieldCheckIcon size={14} weight="fill" aria-hidden />
            {`${replaced} item${replaced === 1 ? '' : 's'} replaced`}
          </span>
        ) : null}
        {/* Figma `622:343262` is a row holding both links. The wrapper is load-bearing, since
            `.info` is a stretch flex column and an unwrapped inline-flex button would span the full
            row width, making the whole line item clickable (AC6). */}
        {unitValue || onViewInstallation ? (
          <div className={styles.links}>
            {unitValue ? (
              <TextButton
                tone="brand"
                size="sm"
                className={styles.link}
                startIcon={<PlusIcon size={12} />}
                // One link per line item means several identically-labelled buttons on a page; the
                // visible text stays Figma's while the accessible name names the device it configures.
                aria-label={`${ADD_PARAMS} for ${item.name}`}
                onClick={() => onAddParams(unitValue)}
              >
                {ADD_PARAMS}
              </TextButton>
            ) : null}
            {onViewInstallation ? (
              <TextButton
                tone="info"
                size="sm"
                className={styles.link}
                aria-label={`${VIEW_INSTALLATION} for ${item.name}`}
                onClick={onViewInstallation}
              >
                {VIEW_INSTALLATION}
              </TextButton>
            ) : null}
          </div>
        ) : null}
        {/* Figma `12:213385`: the count sits below the links row (120977). */}
        <span className={styles.sub}>Qty: {item.quantity}</span>
      </div>
      <div className={styles.prices}>
        {/* Line total, with the per-unit price beneath it (Figma `12:213385`, 120977). */}
        <span className={styles.price}>{formatPrice(item.unitPrice * item.quantity)}</span>
        <span className={styles.each}>Each {formatPrice(item.unitPrice)}</span>
        {item.installationFee != null ? (
          <span className={styles.installation}>
            Installation : {formatPrice(item.installationFee)}
          </span>
        ) : null}
      </div>
    </li>
  );
}

export function OrderSummaryCard({ order }: { order: OrderDetail }) {
  const { lineItems, totals } = order;
  /** Which Unit the flow opens on — and, being nullable, whether it is open at all. One state rather
   *  than a value plus a boolean that can disagree. */
  const [preselect, setPreselect] = useState<string | null>(null);
  const navigate = useNavigate();
  // 120998 — one installation request per order (ADR-0018), keyed by the order id. The admin payload
  // carries no per-line "requested" flag, so a line item's own installation fee — the same field the
  // "Installation : $x" label renders from — marks the line items the request covers. Any status
  // links: a rejected or completed request still exists and is still viewable.
  // 122372 §3g/§3h — nothing to act on before payment. A pre-placement card order can still carry
  // an installation hold, so this gates on status, not on `installationRequest`.
  const viewOnly = isPrePlacementStatus(order.status);
  const viewInstallation =
    order.installationRequest && !viewOnly
      ? () => navigate(routes.requestsInstallationDetail(order.id))
      : undefined;
  // `OrderActionsMenu` already issues this on the same page under the same key, so this is a cache
  // read rather than a second request. Unconditional, like there: gating it on order status would
  // itself be the frontend eligibility logic AC3 forbids (ADR-0024).
  // …except outside the release scope (ADR-0034), where DM Configuration is withheld and neither
  // entry point asks — nor before payment, when there is nothing to configure.
  const { data: devices } = useEligibleDevices(
    { scope: 'order', orderId: order.id, customerId: order.customer.id },
    { enabled: isFeatureEnabled('dm-configuration') && !viewOnly },
  );

  return (
    <section className={styles.card}>
      <h2 className={styles.title}>Order Summary</h2>
      <div className={styles.content}>
        <ul className={styles.items}>
          {lineItems.map((item) => (
            <LineItem
              key={item.id}
              item={item}
              unitValue={firstEligibleUnitValue(devices ?? [], item.id)}
              onAddParams={setPreselect}
              onViewInstallation={item.installationFee != null ? viewInstallation : undefined}
            />
          ))}
        </ul>
        <dl className={styles.totals}>
          <div className={styles.totalRow}>
            <dt>Order Price</dt>
            <dd>{formatPrice(totals.orderPrice)}</dd>
          </div>
          <div className={styles.totalRow}>
            <dt>Installation Fees</dt>
            <dd>{formatPrice(totals.installationFees)}</dd>
          </div>
          {/* Between Installation Fees and Tax (120987). Signed, unlike Figma `538:328087`'s
              unsigned amount, so the summary visibly adds up to the Total. */}
          {totals.discount > 0 ? (
            <div className={styles.totalRow}>
              <dt>Discount</dt>
              <dd>-{formatPrice(totals.discount)}</dd>
            </div>
          ) : null}
          <div className={styles.totalRow}>
            <dt>Tax</dt>
            <dd>{formatPrice(totals.tax)}</dd>
          </div>
          <div className={[styles.totalRow, styles.grandTotal].join(' ')}>
            <dt>Total</dt>
            <dd>{formatPrice(totals.total)}</dd>
          </div>
        </dl>
      </div>
      {/* Mounted **only while open**, unlike the actions menu's always-mounted copy. `useForm` reads
          `defaultValues` once, at mount; an always-mounted modal would freeze `device: ''` at page
          load and every link would open an empty Step 1. Unmounting on close also resets the form
          and the step index for free. */}
      {preselect !== null ? (
        <AddParametersModal
          scope={{ scope: 'order', orderId: order.id, customerId: order.customer.id, preselect }}
          open
          onClose={() => setPreselect(null)}
        />
      ) : null}
    </section>
  );
}
